import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { history } from '@umijs/max';
import { getToken, getUserInfo } from '@/utils/auth';
import { getAppMessage, getAppNotification } from './utils/appContext';

// 错误处理方案： 错误类型
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}
// 与后端约定的响应数据格式
interface ResponseStructure {
  success?: boolean;
  code?: number;
  data: any;
  errorCode?: number;
  errorMessage?: string;
  message?: string;
  showType?: ErrorShowType;
}

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      const {
        success,
        code,
        data,
        errorCode,
        errorMessage,
        message: msg,
        showType,
      } = res as unknown as ResponseStructure;
      // 新的响应格式：code === 0 表示成功，code !== 0 表示失败
      // 旧的响应格式：success === true 表示成功
      const isSuccess = code !== undefined ? code === 0 : success === true;
      if (!isSuccess) {
        const error: any = new Error(errorMessage || msg || '请求失败');
        error.name = 'BizError';
        error.info = {
          errorCode,
          errorMessage: errorMessage || msg,
          showType,
          data,
          code,
        };
        throw error; // 抛出自制的错误
      }
    },
    // 错误接收及处理
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;
      // 我们的 errorThrower 抛出的错误。
      if (error.name === 'BizError') {
        const errorInfo: ResponseStructure | undefined = error.info;
        if (errorInfo) {
          const { errorMessage, errorCode } = errorInfo;
          switch (errorInfo.showType) {
            case ErrorShowType.SILENT:
              // do nothing
              break;
            case ErrorShowType.WARN_MESSAGE:
              getAppMessage().warning(errorMessage);
              break;
            case ErrorShowType.ERROR_MESSAGE:
              getAppMessage().error(errorMessage);
              break;
            case ErrorShowType.NOTIFICATION:
              getAppNotification().open({
                description: errorMessage,
                message: errorCode,
              });
              break;
            case ErrorShowType.REDIRECT:
              // TODO: redirect
              break;
            default:
              getAppMessage().error(errorMessage);
          }
        }
      } else if (error.response) {
        // Axios 的错误
        // 请求成功发出且服务器也响应了状态码，但状态代码超出了 2xx 的范围
        const userInfo = getUserInfo();
        const tokenTemp = getToken();
        if (
          (userInfo && userInfo.token && userInfo.token !== tokenTemp) ||
          (userInfo &&
            userInfo.expiresAt &&
            new Date().getTime() > userInfo.expiresAt)
        ) {
          history.push('/user/login');
        } else {
          getAppMessage().error(`Response status:${error.response.status}`);
        }
      } else if (error.request) {
        // 请求已经成功发起，但没有收到响应
        // \`error.request\` 在浏览器中是 XMLHttpRequest 的实例，
        // 而在node.js中是 http.ClientRequest 的实例
        getAppMessage().error('None response! Please retry.');
      } else {
        // 发送请求时出了点问题
        getAppMessage().error('Request error, please retry.');
      }
    },
  },

  // 请求拦截器
  requestInterceptors: [
    (config: RequestOptions) => {
      // 拦截请求配置，进行个性化处理。
      // 从 localStorage 获取 token 并添加到 header
      const token = getToken();
      if (token) {
        config.headers = {
          ...config.headers,
          Authorization: token,
        };
      }
      return config;
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    (response) => {
      // 拦截响应数据，进行个性化处理
      // 统一响应格式：将新的响应格式 { code, data, message } 转换为 { success, data }
      const { data } = response as unknown as ResponseStructure;

      // 如果响应格式是新的格式（有 code 字段），转换为统一格式
      if (data && typeof data === 'object' && 'code' in data) {
        // 新的响应格式：{ code, data, message, timestamp }
        // 转换为：{ success: code === 0, data, ... }
        return {
          ...response,
          data: {
            ...data,
            success: data.code === 0,
          },
        };
      }

      return response;
    },
  ],
};
